import { useCallback, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { notification } from 'antd';

import WorkJourneyManagementService from '@src/services/WorkJourneyManagementService';

import { useWorkJourneyManagementContext } from '../../../../context';
import type { IWorkJourneyRejectedDrawerUser } from '../../types';

type UseWorkJourneyResolveRejectedChangeParams = {
  initialRejectedUsers: IWorkJourneyRejectedDrawerUser[];
  onIgnoredAll: () => void;
};

export const useWorkJourneyResolveRejectedChange = ({
  initialRejectedUsers,
  onIgnoredAll,
}: UseWorkJourneyResolveRejectedChangeParams) => {
  const intl = useIntl();
  const { orgId, refreshActiveTab } = useWorkJourneyManagementContext();
  const serviceRef = useRef(new WorkJourneyManagementService());

  const [rejectedUsers, setRejectedUsers] = useState(initialRejectedUsers);
  const [busyEventId, setBusyEventId] = useState<string | null>(null);
  const [notifiedOpen, setNotifiedOpen] = useState(false);

  const isBusy = busyEventId != null;

  const handleResend = useCallback(
    async (user: IWorkJourneyRejectedDrawerUser) => {
      if (!orgId || !user.eventId || busyEventId) {
        return;
      }

      setBusyEventId(user.eventId);
      try {
        await serviceRef.current.resendRejectedShiftChange({
          organizationId: orgId,
          eventId: user.eventId,
        });
        setRejectedUsers(prev => prev.filter(entry => entry.eventId !== user.eventId));
        await refreshActiveTab();
        setNotifiedOpen(true);
      } catch (error) {
        console.error('Error resending rejected shift change:', error);
        notification.error({
          message: intl.formatMessage({ id: 'general.save.error.message' }),
        });
      } finally {
        setBusyEventId(null);
      }
    },
    [busyEventId, intl, orgId, refreshActiveTab],
  );

  const handleIgnore = useCallback(
    async (user: IWorkJourneyRejectedDrawerUser) => {
      if (!orgId || !user.eventId || busyEventId) {
        return;
      }

      setBusyEventId(user.eventId);
      try {
        await serviceRef.current.ignoreRejectedShiftChange({
          organizationId: orgId,
          eventId: user.eventId,
        });
        setRejectedUsers(prev => {
          const next = prev.filter(entry => entry.eventId !== user.eventId);
          if (next.length === 0) {
            // Defer close so state updates before navigation.
            queueMicrotask(() => onIgnoredAll());
          }
          return next;
        });
        await refreshActiveTab();
      } catch (error) {
        console.error('Error ignoring rejected shift change:', error);
        notification.error({
          message: intl.formatMessage({ id: 'general.save.error.message' }),
        });
      } finally {
        setBusyEventId(null);
      }
    },
    [busyEventId, intl, onIgnoredAll, orgId, refreshActiveTab],
  );

  const closeNotifiedModal = useCallback(() => {
    setNotifiedOpen(false);
  }, []);

  return useMemo(
    () => ({
      rejectedUsers,
      busyEventId,
      isBusy,
      notifiedOpen,
      handleResend,
      handleIgnore,
      closeNotifiedModal,
    }),
    [
      busyEventId,
      closeNotifiedModal,
      handleIgnore,
      handleResend,
      isBusy,
      notifiedOpen,
      rejectedUsers,
    ],
  );
};
